import * as THREE from 'three';
import { useMemo } from 'react';
import { RigidBody } from '@react-three/rapier';

// Procedural Height Generation
// Shared with texture generation?
// Procedural Height Generation
// Shared with texture generation?

// Math Helper: Distance from point P to segment AB
function distToSegment(px: number, pz: number, x1: number, z1: number, x2: number, z2: number) {
    const l2 = (x1 - x2) ** 2 + (z1 - z2) ** 2;
    if (l2 === 0) return Math.sqrt((px - x1) ** 2 + (pz - z1) ** 2);
    let t = ((px - x1) * (x2 - x1) + (pz - z1) * (z2 - z1)) / l2;
    t = Math.max(0, Math.min(1, t));
    return Math.sqrt((px - (x1 + t * (x2 - x1))) ** 2 + (pz - (z1 + t * (z2 - z1))) ** 2);
}

function distToPolyline(px: number, pz: number, points: Array<[number, number]>) {
    let best = Number.POSITIVE_INFINITY;
    for (let i = 0; i < points.length - 1; i++) {
        const [x1, z1] = points[i];
        const [x2, z2] = points[i + 1];
        const d = distToSegment(px, pz, x1, z1, x2, z2);
        if (d < best) best = d;
    }
    return best;
}

const HUB_RADIUS = 22;
const ROAD_WIDTH = 5;
const RING_ROAD_RADIUS = 38;
const LAKE_CENTER: [number, number] = [150, 40];
const LAKE_RADIUS = 28;

const ROAD_PATHS: Array<Array<[number, number]>> = [
    // West forest to GA
    [[0, 0], [-40, 5], [-90, 0], [-140, -10]],
    // North-west to AP
    [[0, 0], [-30, 30], [-70, 70], [-120, 90]],
    // East manor (AB)
    [[0, 0], [50, 10], [110, 20], [160, 10]],
    // South keep (CP)
    [[0, 0], [10, -40], [40, -90], [60, -140]],
    // South-west to LA (arena)
    [[0, 0], [-30, -30], [-80, -80], [-130, -140]],
    // South inner to SI
    [[0, 0], [0, -40], [-10, -80]]
];

// Map Logic: Is this point on a road?
export function isRoad(x: number, z: number) {
    // Central hub
    if (Math.sqrt(x * x + z * z) < HUB_RADIUS) return true;

    // Ring road around the central city
    const ringDist = Math.abs(Math.sqrt(x * x + z * z) - RING_ROAD_RADIUS);
    if (ringDist < ROAD_WIDTH) return true;

    // Radial roads based on sketch
    for (const path of ROAD_PATHS) {
        if (distToPolyline(x, z, path) < ROAD_WIDTH) return true;
    }

    return false;
}

// Map Logic: River
export function isRiver(x: number, z: number) {
    // River flowing from north-east mountains to the south
    const riverPath: Array<[number, number]> = [
        [210, 140],
        [180, 90],
        [150, 50],
        [110, 10],
        [70, -20],
        [30, -70],
        [-10, -120],
        [-40, -170]
    ];
    const riverWidth = 10;
    return distToPolyline(x, z, riverPath) < riverWidth;
}

// Map Logic: Static Objects (Houses, Fireplace, etc.)
export function isObject(x: number, z: number) {
    // Center fireplace area
    if (Math.sqrt(x * x + z * z) < 10) return true;

    // Houses and key points (approx radius for safety)
    const points = [
        { x: -140, z: -10 }, // About (GA)
        { x: 60, z: -140 },  // Personal Projects (CP)
        { x: 160, z: 10 },   // Apps (AB)
        { x: -120, z: 90 },  // Games (AP)
        { x: -130, z: -140 },// Simulations (LA)
        { x: -10, z: -80 },  // SI (village)
        { x: 40, z: 40 }     // RE (ruins)
    ];
    for (const p of points) {
        if (Math.sqrt((x - p.x) ** 2 + (z - p.z) ** 2) < 18) return true;
    }

    return false;
}

// Map Logic: Pond
export function isPond(x: number, z: number) {
    // Lake in the north-east
    const dist = Math.sqrt((x - LAKE_CENTER[0]) ** 2 + (z - LAKE_CENTER[1]) ** 2);
    return dist < LAKE_RADIUS;
}

// Update getHeight to respect River
export function getHeight(x: number, z: number) {
    if (isRiver(x, z)) return -8; // River Channel
    if (isPond(x, z)) return -8; // Pond Bed

    const dist = Math.sqrt(x * x + z * z);
    if (dist < 22) return 0; // Flat village center

    // Gentle rolling hills (flattened from rolling hills)
    return Math.sin(x * 0.1) * 0.5 + Math.cos(z * 0.1) * 0.5 + (dist - 20) * 0.05;
}

import type { ThreeEvent } from '@react-three/fiber';

export const Terrain = ({ onClick }: { onClick?: (e: ThreeEvent<MouseEvent>) => void }) => {
    const size = 600;
    const segments = 200;

    // 1. Generate Geometry
    const geometry = useMemo(() => {
        const geo = new THREE.PlaneGeometry(size, size, segments, segments);
        const pos = geo.attributes.position;
        for (let i = 0; i < pos.count; i++) {
            const x = pos.getX(i);
            const y = pos.getY(i);
            // Rotated mesh: worldZ = -localY. So we check height at (x, -y)
            const h = getHeight(x, -y);
            pos.setZ(i, h);
        }
        geo.computeVertexNormals();
        return geo;
    }, []);

    // 2. Generate Texture
    const texture = useMemo(() => {
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        // Fill Grass
        ctx.fillStyle = '#4a9c4a';
        ctx.fillRect(0, 0, 512, 512);

        // Draw Dirt/Roads
        const imgData = ctx.getImageData(0, 0, 512, 512);
        const data = imgData.data;

        for (let y = 0; y < 512; y++) {
            for (let x = 0; x < 512; x++) {
                // Map UV to World Coords
                // UV (0..1) -> World (-size/2 .. size/2)
                const worldX = (x / 512 - 0.5) * size;
                const worldZ = -(y / 512 - 0.5) * size; // Flip Y for Texture Z

                if (isRiver(worldX, worldZ) || isPond(worldX, worldZ)) {
                    const index = (y * 512 + x) * 4;
                    // Sand/Riverbed color
                    data[index] = 194;
                    data[index + 1] = 178;
                    data[index + 2] = 128;
                } else if (isRoad(worldX, worldZ)) {
                    const index = (y * 512 + x) * 4;
                    // Brown color #8B4513 (139, 69, 19)
                    // Add some noise
                    const noise = Math.random() * 20;
                    data[index] = 139 + noise;
                    data[index + 1] = 69 + noise;
                    data[index + 2] = 19 + noise;
                } else {
                    // Grass Noise
                    const index = (y * 512 + x) * 4;
                    const noise = Math.random() * 30 - 15;
                    data[index] = Math.min(255, Math.max(0, 74 + noise));  // R
                    data[index + 1] = Math.min(255, Math.max(0, 156 + noise)); // G
                    data[index + 2] = Math.min(255, Math.max(0, 74 + noise)); // B
                }
            }
        }
        ctx.putImageData(imgData, 0, 0);
        const tex = new THREE.CanvasTexture(canvas);
        tex.wrapS = THREE.RepeatWrapping;
        tex.wrapT = THREE.RepeatWrapping;
        return tex;
    }, []);

    return (
        <RigidBody type="fixed" colliders="trimesh">
            <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow geometry={geometry} onClick={onClick}>
                <meshStandardMaterial
                    map={texture}
                    roughness={1}
                />
            </mesh>
        </RigidBody>
    );
};
